import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
  untracked,
} from "@angular/core";
import { getDefaultAvatarUrl } from "../../../core/constants/avatars.constant";

@Component({
  selector: "app-user-avatar",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!failed()) {
      <img
        class="uav-img"
        [src]="src()"
        [alt]="name()"
        (error)="failed.set(true)"
      />
    } @else {
      <div class="uav-fallback" [style.background]="fallback()">
        {{ initial() }}
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
        border-radius: inherit;
        overflow: hidden;
      }
      .uav-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .uav-fallback {
        width: 100%;
        height: 100%;
        display: grid;
        place-items: center;
        font-weight: 700;
        font-size: 40%;
        color: white;
        user-select: none;
      }
    `,
  ],
})
export class UserAvatarComponent {
  readonly name = input<string>("");
  readonly avatarUrl = input<string | null>(null);

  readonly failed = signal(false);

  // Use || not ?? — empty string "" from socket must fall through to the default
  readonly src = computed(
    () => this.avatarUrl() || getDefaultAvatarUrl(this.name()),
  );

  constructor() {
    // Reset error state whenever the source URL changes.
    // Must use effect + untracked — writing to a signal inside computed() throws.
    effect(() => {
      this.src();
      untracked(() => this.failed.set(false));
    });
  }

  readonly initial = computed(
    () => this.name().slice(0, 1).toUpperCase() || "?",
  );
  readonly fallback = computed(() => {
    const colors = [
      "linear-gradient(135deg,#f472b6,#c084fc)",
      "linear-gradient(135deg,#818cf8,#6366f1)",
      "linear-gradient(135deg,#34d399,#059669)",
      "linear-gradient(135deg,#fb923c,#ea580c)",
      "linear-gradient(135deg,#60a5fa,#3b82f6)",
    ];
    let h = 0;
    for (let i = 0; i < this.name().length; i++) {
      h = (Math.imul(31, h) + this.name().charCodeAt(i)) | 0;
    }
    return colors[Math.abs(h) % colors.length];
  });
}
